// Generate-document button shown on Aufnahme / Besichtigung appointment rows.
// On click:
//   1. POST /documents with the appropriate type → atomic number assigned in D1
//   2. Fetch fresh client + property data
//   3. Render PDF (Vermittlungsauftrag for aufnahme, Besichtigungsschein for
//      besichtigung) with the assigned number embedded
//   4. Download to disk
//
// The DB row is the source of truth for the number — even if the user cancels
// the download, the number is reserved (gaps are acceptable, duplicates are not).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ClipboardSignature, FileText, Loader2 } from 'lucide-react';
import {
  useCreateDocument,
  type DocumentRow,
} from '@/hooks/data/useDocuments';
import { apiFetch } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientRow } from '@/hooks/data/useClients';
import type { PropertyRow } from '@/hooks/data/useProperties';
// PDF generator is dynamic-imported on click — pulls @react-pdf/renderer
// (~1.46 MB) out of the route's initial bundle.
type PdfModule = typeof import('@/pdf/offerPdfGenerator');
const loadPdf = (): Promise<PdfModule> => import('@/pdf/offerPdfGenerator');
import { SignDocumentDialog } from '@/components/documents/SignDocumentDialog';
import {
  VermittlungsauftragEditDialog,
  DEFAULT_VERMITTLUNGSAUFTRAG_FORM,
  type VermittlungsauftragFormValues,
} from '@/components/appointments/VermittlungsauftragEditDialog';

interface Props {
  appointmentId: string;
  appointmentDate: number;
  type: 'aufnahme' | 'besichtigung';
  clientId: string;
  propertyId?: string | null;
  agentName: string;
  agentPhone?: string | null;
  /** v2 — drop the long German label and render an icon-only button so
   *  the action column on the desktop appointments table fits in the
   *  viewport. The full label stays in the `title` attribute. */
  compact?: boolean;
  /** Zoki draft hand-off: prefills the Vermittlungsauftrag dialog with the
   *  options the assistant already collected, and reports the finished
   *  document back so the caller can mark the draft consumed. */
  draftDefaults?: Partial<VermittlungsauftragFormValues>;
  onGenerated?: (documentId: string) => void;
  /** Overrides the button label — the draft banner has its own wording. */
  labelOverride?: string;
}

async function fetchClient(id: string): Promise<ClientRow> {
  const res = await apiFetch(`/clients/${id}`);
  if (!res.ok) throw new Error(`client_fetch_failed`);
  return (await res.json()) as ClientRow;
}

async function fetchProperty(id: string): Promise<PropertyRow> {
  const res = await apiFetch(`/properties/${id}`);
  if (!res.ok) throw new Error(`property_fetch_failed`);
  return (await res.json()) as PropertyRow;
}

export function GenerateDocumentButton({
  appointmentId,
  appointmentDate,
  type,
  clientId,
  propertyId,
  agentName,
  agentPhone,
  compact = false,
  draftDefaults,
  onGenerated,
  labelOverride,
}: Props) {
  const { t } = useTranslation();
  const create = useCreateDocument();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState<DocumentRow | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; lang: string } | null>(
    null,
  );
  // Pre-flight edit dialog (Aufnahme only). Batch AJ-6 replaced the
  // small 2-field dialog with `VermittlungsauftragEditDialog` covering
  // every user-editable field of the document. State is the resolved
  // form payload; null means the dialog is closed.
  const [vaForm, setVaForm] = useState<VermittlungsauftragFormValues | null>(null);

  const isAufnahme = type === 'aufnahme';
  const docType = isAufnahme ? 'vermittlungsauftrag' : 'besichtigungsschein';
  const label =
    labelOverride ??
    (isAufnahme
      ? t('docs.aufnahme.cta', 'Vermittlungsauftrag generieren')
      : t('docs.besichtigung.cta', 'Besichtigungsschein generieren'));

  const onClick = async () => {
    if (busy) return;
    // Aufnahme opens the full edit dialog. Besichtigung has no
    // pre-flight inputs, fire directly.
    if (isAufnahme) {
      // Zoki-prepared drafts arrive with the options already chosen; the
      // dialog still opens so the makler reviews them before anything is
      // generated — the assistant proposes, the human signs off.
      setVaForm({ ...DEFAULT_VERMITTLUNGSAUFTRAG_FORM, ...(draftDefaults ?? {}) });
      return;
    }
    await runGenerate(null);
  };

  const runGenerate = async (formValues: VermittlungsauftragFormValues | null) => {
    setBusy(true);
    try {
      // 1. Reserve number via API. For Aufnahme, persist the full form
      // payload into documents.metadata so a re-render after signing
      // reproduces the exact same content the seller saw (bytes are
      // additionally locked in R2 once signed — see AppointmentDocuments
      // onDownloadSigned + /documents/:id/lock-pdf — but the metadata
      // ensures the legacy fallback re-render path is also faithful).
      const metadata = formValues ? { vermittlungsauftrag: formValues } : undefined;
      const doc = await create.mutateAsync({
        type: docType,
        // Migration 0058 — the variant travels as a COLUMN as well as inside
        // metadata. The column is what the daily performance report counts;
        // metadata is what a re-render reads. Writing both from the same
        // form payload is what keeps them from ever disagreeing.
        ...(isAufnahme && formValues
          ? { contractVariant: formValues.contractVariant }
          : {}),
        clientId,
        propertyId: propertyId ?? null,
        appointmentId,
        metadata,
      });

      // 2. Fetch fresh client + property
      const [client, property] = await Promise.all([
        fetchClient(clientId),
        propertyId ? fetchProperty(propertyId) : Promise.resolve(null),
      ]);

      // 3. Render PDF in 10 languages. The orchestrator returns the DE
      // blob synchronously (so we can trigger the local download right
      // away) and runs the other 9 renders + uploads in the background.
      // The DE upload is included in the orchestrator — no separate call.
      let deBlob: Blob;
      let fileName: string;
      let backgroundPromise: Promise<void>;
      const onProgress = (lang: string, done: number, total: number) => {
        setProgress({ done, total, lang });
      };
      if (isAufnahme) {
        if (!property) throw new Error(t('docs.errors.property_missing', 'Property fehlt für Vermittlungsauftrag'));
        if (!formValues)
          throw new Error('Vermittlungsauftrag-Formular fehlt');
        const data = {
          documentNumber: doc.number,
          agentName,
          agentPhone: agentPhone ?? null,
          date: appointmentDate,
          seller: {
            title: client.title,
            first_name: client.first_name,
            last_name: client.last_name,
            address: client.address,
            location: client.location,
            phone: client.phone,
            email: client.email,
            birth_date: client.birth_date,
            citizenship: client.citizenship,
            sv_number: client.sv_number,
            // Form-only fields (overlay on top of client row)
            plz: formValues.seller.plz,
            konsument: formValues.seller.konsument,
            eigentumsstatus: formValues.seller.eigentumsstatus,
          },
          property: {
            // Static fields from the property record
            address: property.address,
            city: property.city,
            postal_code: property.postal_code,
            district: property.district,
            entrance: property.entrance,
            floor: property.floor,
            door_number: property.door_number,
            object_type: property.object_type,
            building_type: property.building_type,
            rooms_count: property.rooms_count,
            property_size: property.property_size,
            property_area: property.property_area,
            purchase_price: property.purchase_price,
            edi_real_number: property.edi_real_number,
            kg_grundbuch: property.kg_grundbuch,
            einlagezahl: property.einlagezahl,
            b_laufnummer: property.b_laufnummer,
            // User-editable overlay from the dialog
            ...formValues.property,
          },
          contractVariant: formValues.contractVariant,
          provisionNetPercent: formValues.provisionNetPercent,
          ustPercent: 20,
          exclusivityMonths: formValues.exclusivityMonths,
          gerichtsstand: formValues.gerichtsstand,
          bestaetigung: formValues.bestaetigung,
          faggVorzeitig: formValues.faggVorzeitig,
        };
        const pdf = await loadPdf();
        const result = await pdf.generateAllVermittlungsauftrag(doc.id, data, {
          onProgress,
          onError: (lang, err) => console.warn(`[gen-doc] ${lang} failed`, err),
        });
        deBlob = result.deBlob;
        backgroundPromise = result.backgroundPromise;
        fileName =
          formValues.contractVariant === 'einfach'
            ? `Vermittlungsauftrag-${doc.number}-${client.last_name}.pdf`
            : `Alleinvermittlungsauftrag-${doc.number}-${client.last_name}.pdf`;
      } else {
        const data = {
          documentNumber: doc.number,
          agentName,
          agentPhone: agentPhone ?? null,
          date: appointmentDate,
          buyer: {
            title: client.title,
            first_name: client.first_name,
            last_name: client.last_name,
            address: client.address,
            location: client.location,
            phone: client.phone,
            email: client.email,
          },
          property: property
            ? {
                address: property.address,
                city: property.city,
                postal_code: property.postal_code,
                district: property.district,
                entrance: property.entrance,
                floor: property.floor,
                door_number: property.door_number,
                object_type: property.object_type,
                edi_real_number: property.edi_real_number,
              }
            : {
                address: null,
                city: '—',
                postal_code: null,
                district: null,
                entrance: null,
                floor: null,
                door_number: null,
                object_type: null,
                edi_real_number: null,
              },
        };
        const pdf = await loadPdf();
        const result = await pdf.generateAllBesichtigungsschein(doc.id, data, {
          onProgress,
          onError: (lang, err) => console.warn(`[gen-doc] ${lang} failed`, err),
        });
        deBlob = result.deBlob;
        backgroundPromise = result.backgroundPromise;
        fileName = `Besichtigungsschein-${doc.number}-${client.last_name}.pdf`;
      }

      // Trigger local download for the binding DE version. The other 9
      // languages keep rendering + uploading in the background; we
      // invalidate the documents query when they're done so any
      // per-language download chips refresh.
      const url = URL.createObjectURL(deBlob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = fileName;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);

      void backgroundPromise.finally(() => {
        setProgress(null);
        // Other languages may have just been uploaded — refresh any
        // appointment-document lists so they pick up `available_languages`.
        qc.invalidateQueries({ queryKey: ['documents'] });
      });

      onGenerated?.(doc.id);
      toast.success(`${label} ${t('docs.generated', 'erstellt')}: Nr. ${doc.number}`, {
        action: {
          label: t('docs.sign_now', 'Unterschreiben'),
          onClick: () => setSigning(doc),
        },
        duration: 8000,
      });
    } catch (cause) {
      toast.error((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Phase E: while DE has finished but the background queue is still
  // working through the other 9 languages, surface a small inline pill so
  // the makler knows translations are still being prepared. The DE PDF
  // already downloaded; this is purely informational.
  const showProgress = progress !== null && progress.done < progress.total;
  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          title={label}
          className={
            compact
              ? 'inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 transition hover:bg-paper-2 disabled:opacity-60'
              : 'inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-2.5 py-1 text-xs font-medium text-ink transition hover:bg-paper-2 disabled:opacity-60'
          }
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : isAufnahme ? (
            <FileText className="h-3 w-3" />
          ) : (
            <ClipboardSignature className="h-3 w-3" />
          )}
          {!compact && label}
        </button>
        {showProgress && (
          <span className="inline-flex items-center gap-1 rounded-full border border-line bg-paper-2 px-2 py-0.5 text-[10px] font-medium text-ink-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('docs.lang_progress', 'Sprache {{done}}/{{total}}', {
              done: progress!.done,
              total: progress!.total,
            })}
          </span>
        )}
      </span>
      {signing && (
        <SignDocumentDialog
          document={signing}
          open={true}
          onOpenChange={(o) => !o && setSigning(null)}
        />
      )}
      {/* Batch AJ-6 — full pre-flight edit form. */}
      <VermittlungsauftragEditDialog
        open={vaForm !== null}
        defaults={vaForm ?? DEFAULT_VERMITTLUNGSAUFTRAG_FORM}
        onCancel={() => setVaForm(null)}
        onGenerate={(values) => {
          setVaForm(null);
          void runGenerate(values);
        }}
      />
    </>
  );
}
